import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Read Portal Evidence Documents Step
 *
 * Classifies and extracts the text of documents an operator downloaded from a
 * customer's vendor portal, persisting both onto each Document and its OCR
 * result row.
 *
 * Belongs BETWEEN `web_browser` and `file_collection_evidence_documents`. A
 * portal download lands as a bare Document — bytes, a size, a filename — and
 * everything downstream reads what that doesn't set: the recommendation the
 * filing step refreshes feeds on `classificationType` plus the extraction, and
 * so does the thread's document-naming agent. Reading first is what makes the
 * fetched evidence legible; file first and the recommendation regenerates
 * against documents nothing can see inside.
 *
 * Distinct from `extract_document_content_v4`, which runs Extend AI against a
 * registered schema definition and skips when no schema matches the document's
 * classification. This step needs no schema registered: it prompts a vision
 * model with the org's configured field list (Settings → Organization → AR
 * Collections), defaulting to one tuned for deduction backup.
 *
 * Passes `documentIds` straight through, so it drops into an existing action
 * workflow between the two steps without re-wiring what the filing step reads.
 */
export const readPortalEvidenceDocumentsStep = defineStepConfig({
  type: "read_portal_evidence_documents",
  name: "Read Portal Evidence Documents",
  description:
    "Classifies and extracts operator-downloaded portal documents so the thread's recommendation and document names can see inside them",
  category: "AI & Agents",
  libraryModule: "AccountsReceivable",

  inputSchema: z.object({
    documentIds: z
      .array(z.string())
      .default([])
      .describe(
        "Document ids harvested from an operator run. Wire from web_browser's documentIds output.",
      ),
  }),

  outputSchema: z.object({
    documentIds: z
      .array(z.string())
      .describe(
        "The same ids, passed through so the filing step can wire from here instead of reaching back past this step.",
      ),
    read: z
      .number()
      .int()
      .nonnegative()
      .describe("Documents that came back with extracted content"),
    alreadyRead: z
      .number()
      .int()
      .nonnegative()
      .describe(
        "Documents that already had a completed extraction (a retry re-running over the same ids)",
      ),
    failed: z
      .number()
      .int()
      .nonnegative()
      .describe("Documents whose extraction failed or errored"),
  }),

  configSchema: z.object({}),

  execution: {
    // One vision call per document against a whole PDF, run sequentially.
    // A multi-document portal fetch runs well past the 5-minute default; the
    // handler heartbeats between documents so the 2-minute heartbeat timeout
    // still catches a dead worker.
    startToCloseTimeout: "20 minutes",
  },

  emits: [] as const,
});
